import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { BrandLoader } from '@/shared/ui/BrandLoader';
import { Button } from '@/shared/ui/Button';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { BadgeTone } from '@/shared/ui/StatusBadge';
import type { HarvestState } from '@/shared/api/generated/catalog';

import { useHarvest } from '../api/connections';
import { harvestBar } from '../model/listing';
import type { HarvestPhase } from '../model/listing';

/* Six states, five distinct tones, and the distinctions are the point.
 *
 * `partial` is `warn`, not `ok`: a harvest that reached the source but could not read every object
 * produced a catalog that is incomplete in ways nobody has been told about, and the platform
 * deliberately refuses to infer absence from it. Showing it in green would erase exactly the fact the
 * state exists to carry.
 *
 * `cancelled` is `neutral`, not `danger`: an operator stopping a harvest is a decision, not a fault,
 * and alerting on it trains people to ignore harvest alerts.
 */
const STATE_TONE: Readonly<Record<HarvestState, BadgeTone>> = {
  pending: 'info',
  running: 'info',
  succeeded: 'ok',
  partial: 'warn',
  failed: 'danger',
  cancelled: 'neutral',
};

/* The bar's fill colour per phase: green throughout, so the filled share reads as the work done
 * while the badge beside it carries whether the run is still going. Only the phases that draw a
 * measured bar appear; `listing` draws the hatched track instead, and the terminal phases other than
 * `succeeded` draw none, because a bar beside "completed with errors" would only restate the
 * failure count in a second notation. */
const FILL: Readonly<Partial<Record<HarvestPhase, 'ok'>>> = {
  reading: 'ok',
  saving: 'ok',
  succeeded: 'ok',
};

/* A plain ICU argument stringifies rather than formats, so a seven-digit count would arrive with no
 * grouping — the one figure that cannot be read at a glance. Grouped here instead, and an unreadable
 * count is a dash rather than a zero, which would claim the source was empty. */
function figure(value: number | undefined): string {
  return value === undefined ? '—' : value.toLocaleString();
}

/** The Harvest card on a source's page: one row saying what the run is doing and how far it got.
 *
 * Polls through `useHarvest` until the run reaches a terminal state, and calls `onFinished` once
 * when it does, so the page can refetch the datasets the run just wrote.
 */
export function HarvestProgress({
  harvestId,
  onDismiss,
  onFinished,
}: {
  readonly harvestId: string;
  readonly onDismiss: () => void;
  readonly onFinished?: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const harvest = useHarvest(harvestId);

  const operation = harvest.data;
  const bar = operation === undefined ? null : harvestBar(operation);
  const done = bar !== null && !bar.inFlight;

  /* Once per run, on the transition into a terminal state — including a run that was already
   * finished when the card mounted, since the datasets it wrote may not be on screen yet. Keyed on
   * the id rather than a boolean so a second harvest in the same session reports its own finish. */
  const reported = useRef<string | null>(null);
  useEffect(() => {
    if (done && reported.current !== harvestId) {
      reported.current = harvestId;
      onFinished?.();
    }
  }, [done, harvestId, onFinished]);

  /* The objects that could not be read, counted from the progress rather than from the list: the
   * list holds one entry for everything past the object cap, so its length undercounts. */
  const unread =
    operation === undefined ? 0 : (bar?.counts?.failed ?? operation.failures.length);

  return (
    <div className="lq-card lq-harvestbar">
      <div className="lq-harvestbar__row">
        {/* The loader while the run is in flight, and only then: a finished harvest is a result. */}
        {bar?.inFlight === true ? <BrandLoader size="md" /> : null}
        <HelpLabel subject={t('harvest.title')} help={t('help.feature.harvest')}>
          <strong className="lq-harvestbar__title">{t('harvest.title')}</strong>
        </HelpLabel>
        {operation !== undefined && bar !== null ? (
          <>
            <StatusBadge
              tone={STATE_TONE[operation.state]}
              label={t(`harvest.state.${operation.state}.label`)}
              help={t(`harvest.state.${operation.state}.meaning`)}
            />
            {/* The live region is the activity alone. The whole card used to be one, which was
                harmless while its counts never moved; now they change on every poll, and announcing
                each new figure would talk over a screen-reader user every two seconds. The phrase
                changes only when the run moves to its next phase. */}
            <span className="lq-harvestbar__activity" role="status" aria-live="polite">
              {t(`harvest.activity.${bar.phase}`)}
            </span>

            {bar.phase === 'listing' ? (
              <span className="lq-harvestbar__meter">
                <span
                  className="lq-runbar lq-runbar--queued lq-harvestbar__track"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={t('harvest.listingLabel')}
                />
              </span>
            ) : null}

            {bar.percent !== null && FILL[bar.phase] !== undefined ? (
              <span className="lq-harvestbar__meter">
                <span
                  className="lq-runbar lq-harvestbar__track"
                  role="progressbar"
                  aria-valuenow={bar.percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={t('harvest.progressLabel', {
                    processed: figure(
                      bar.counts === null
                        ? undefined
                        : bar.counts.observed + bar.counts.failed,
                    ),
                    discovered: figure(bar.counts?.discovered),
                  })}
                >
                  <span
                    className={`lq-runbar__fill lq-runbar__fill--${FILL[bar.phase] ?? 'ok'}`}
                    style={{ width: `${String(bar.percent)}%` }}
                  />
                </span>
                <span className="lq-harvestbar__percent lumiq-numeric">
                  {t('harvest.percent', { percent: bar.percent })}
                </span>
              </span>
            ) : null}

            <span className="lq-harvestbar__counts lumiq-numeric">
              {t('harvest.progress', {
                discovered: figure(bar.counts?.discovered),
                observed: figure(bar.counts?.observed),
                failed: figure(bar.counts?.failed),
              })}
            </span>
          </>
        ) : null}
        {done ? (
          <Button size="compact" onClick={onDismiss}>
            {t('harvest.dismiss')}
          </Button>
        ) : null}
      </div>

      {harvest.isError ? (
        <div className="lq-harvestbar__line">
          <Alert tone="error">{harvest.error.message}</Alert>
        </div>
      ) : null}

      {operation !== undefined ? (
        <>
          {operation.absence_withheld > 0 ? (
            /* The fact that makes `partial` actionable. "Your catalog may be stale by N datasets" is
             * what an operator needs in order to decide whether to re-run — and it is reported even
             * when zero, so a client never has to distinguish "nothing withheld" from "not
             * reported". */
            <p className="lq-harvestbar__line lq-harvestbar__line--warn">
              {t('harvest.absenceWithheld', { count: operation.absence_withheld })}
            </p>
          ) : null}

          {/* The server's own sentence, for the outcomes it explains: why a run failed, or what an
              operator said when stopping it. Host-written — a failure carries only the exception
              type, never a driver message or a DSN. Not shown for `partial`, whose detail is the
              failure count the details line below already gives, with the objects named. */}
          {operation.detail !== null &&
          (operation.state === 'failed' || operation.state === 'cancelled') ? (
            <p className="lq-harvestbar__line">{operation.detail}</p>
          ) : null}

          {operation.failures.length > 0 ? (
            <details className="lq-harvestbar__line">
              <summary className="lq-harvestbar__failures">
                {t('harvest.failures', { count: unread })}
              </summary>
              <ul className="lq-harvestbar__failureList">
                {operation.failures.map((failure) => (
                  <li key={failure.qualified_name}>
                    <span className="lumiq-numeric">{failure.qualified_name}</span>
                    {' — '}
                    <span className="lq-dim">{failure.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
